import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

import { mapBankOfferFromApi } from '@/components/App/NewOrder/helpers/mapBankOfferFromApi';
import type { TBankOffers } from '@/components/App/NewOrder/offer-step';
import type { ISapaAdditionalProducts } from '@/components/App/NewOrder/offer-step/features/sapa-bank/types/sapa.types';
import { BankNames } from '@/constants';
import { autoCreditApi } from '@/integrations/app.api/auto-credit.api';
import { checkIsValidJSON } from '@/services/helpers/checkIsValidJSON';
import { parseAutoCredLeasingResponse } from '@/services/mappers/autoCredMapper';
import type {
  ChangeBankStatusInterface,
  OfferResponseFromApi
} from '@/types/offers';
import type { TOfferStepStore } from '@/types/store';

import { createAsyncThunk } from './create-async-thunk';
import { getAutoCredLeasingResponsesByRequestById } from './newOrder.slice';

export const getResponsesByRequestId = createAsyncThunk(
  'offerStep/getResponsesByRequestId',
  async ({
    reqId,
    abortSignal
  }: {
    reqId: string;
    abortSignal?: AbortSignal;
  }): Promise<OfferResponseFromApi[]> => {
    const data = await autoCreditApi.getResponsesByRequestId(
      reqId,
      abortSignal
    );

    const shihanOfferIndex = data.findIndex(
      (response) => response.bank.name === 'shinhan'
    );
    const shinHanOffer = data[shihanOfferIndex];
    if (
      shinHanOffer &&
      (shinHanOffer.status?.code === 'approved' ||
        shinHanOffer?.status?.code === 'accepted_alternative' ||
        shinHanOffer.status?.code === 'waiting_of_financing' ||
        shinHanOffer.status?.code === 'mobile_client')
    ) {
      const responseApprovedData = JSON.parse(
        shinHanOffer.response_approved_body || '{}'
      );
      data[shihanOfferIndex] = {
        ...data[shihanOfferIndex],
        rate: responseApprovedData?.interest_rate || '',
        monthly_payment: responseApprovedData?.monthly_payment || ''
      };
    }

    const sapabankOfferIndex = data.findIndex(
      (response) => response.bank.name === BankNames.sapa
    );
    const sapabankOffer = data[sapabankOfferIndex];
    if (sapabankOffer) {
      const checkedSapaApprovedBody = checkIsValidJSON(
        sapabankOffer.response_approved_body || ''
      );
      const parsedSapaApprovedBody: ISapaAdditionalProducts | null =
        checkedSapaApprovedBody
          ? JSON.parse(sapabankOffer.response_approved_body || '{}')
          : null;
      if (parsedSapaApprovedBody?.offerType) {
        data[sapabankOfferIndex] = {
          ...data[sapabankOfferIndex],
          ext_uuid: String(parsedSapaApprovedBody.offerType)
        };
      }
    }
    return data;
  }
);

export const getEuBankStatus = createAsyncThunk(
  'offerStep/getEuBankStatus',
  async (
    data: { requestId: string; responseExtUuid: string },
    { dispatch }
  ) => {
    const response = await autoCreditApi.getEuBankStatus(data);
    dispatch(getResponsesByRequestId({ reqId: data.requestId }));
    return response;
  }
);

export const getForteStatus = createAsyncThunk(
  'offerStep/getForteStatus',
  async ({
    extUuid,
    controlClientDecision
  }: {
    extUuid: string;
    controlClientDecision?: boolean;
  }) => {
    return autoCreditApi.getForteStatus(extUuid, controlClientDecision);
  }
);

export const acceptOffer = createAsyncThunk(
  'offerStep/acceptOffer',
  async (data: { responseId: string; offerId?: string }) => {
    return autoCreditApi.acceptOffer(data.responseId, data.offerId);
  }
);

export const acceptLeasingOffer = createAsyncThunk(
  'offerStep/acceptLeasingOffer',
  async (responseId: string) => {
    return autoCreditApi.acceptLeasingOffer(responseId);
  }
);

export const changeOfferStatus = createAsyncThunk(
  'offerStep/changeOfferStatus',
  async (data: ChangeBankStatusInterface) => {
    return autoCreditApi.changeOfferStatus(data);
  }
);

const initialState: TOfferStepStore = {
  banksOffers: [],
  isLoading: false,
  shouldUpdateResponses: false,
  leasingOffers: []
};

export const offerStepSlice = createSlice({
  name: 'offerStep',
  initialState,
  reducers: {
    setBankOffers: (state, action: PayloadAction<TBankOffers[]>) => {
      state.banksOffers = action.payload;
    },
    setShouldUpdateResponses: (state, action: PayloadAction<boolean>) => {
      state.shouldUpdateResponses = action.payload;
    }
  },

  extraReducers: (builder) => {
    builder
      .addCase(getResponsesByRequestId.fulfilled, (state, action) => {
        state.banksOffers = action.payload.map(mapBankOfferFromApi);
      })

      .addCase(
        getAutoCredLeasingResponsesByRequestById.fulfilled,
        (state, action) => {
          state.leasingOffers =
            action.payload[0]?.responses?.map(parseAutoCredLeasingResponse) ||
            [];
        }
      )

      .addCase(changeOfferStatus.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(changeOfferStatus.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(changeOfferStatus.fulfilled, (state) => {
        state.isLoading = false;
      })

      .addCase(getEuBankStatus.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getEuBankStatus.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getEuBankStatus.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(getForteStatus.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getForteStatus.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getForteStatus.fulfilled, (state, action) => {
        state.isLoading = false;

        const forteOffer = action.payload.find(
          (response: { bank: { name: string } }) =>
            response.bank.name === 'forte'
        );
        if (!forteOffer) return state;

        const newBankOffers = [...state.banksOffers];
        const forteOfferIndex = newBankOffers.findIndex(
          (bank) => bank.bank.name === 'forte'
        );
        newBankOffers[forteOfferIndex] = mapBankOfferFromApi(forteOffer);
        state.banksOffers = newBankOffers;
      });
  }
});

export const { setBankOffers, setShouldUpdateResponses } =
  offerStepSlice.actions;
